import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Heading, Text, Button, Stack, HStack, Badge, Icon, Spinner } from "@chakra-ui/react";
import { Plus, Pencil, Eye, Trash2, ShieldCheck, Building2, ClipboardList, ListChecks, Globe, Target, ArrowUpRight, Copy } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { TemplatePreview } from "@/components/TemplatePreview";
import { ListManager } from "@/components/ListManager";
import { SegmentedTabs } from "@/components/SegmentedTabs";
import { countFields, emptySchema } from "@/lib/templateFields";
import { colors } from "@/theme/colors";

const TABS = [
  { key: "templates", label: "Templates", icon: ClipboardList },
  { key: "lists", label: "Custom Lists", icon: ListChecks },
];

const Banner = ({ msg }) =>
  msg ? (
    <Box mb="4" px="4" py="2.5" borderRadius="10px" fontSize="sm"
      bg={msg.type === "error" ? colors.criticalBg : colors.resolvedBg}
      color={msg.type === "error" ? colors.criticalText : colors.resolvedText}
      data-testid="templates-banner">
      {msg.text}
    </Box>
  ) : null;

const StatusBadge = ({ status }) => (
  <Badge bg={status === "published" ? colors.resolvedBg : colors.pendingBg}
    color={status === "published" ? colors.resolvedText : colors.pendingText}
    borderRadius="full" px="2.5" textTransform="capitalize">{status}</Badge>
);

const ScopeBadge = ({ scope }) =>
  scope === "specialized" ? (
    <Badge bg={colors.pendingBg} color={colors.pendingText} borderRadius="full" px="2.5" data-testid="scope-badge-specialized">
      <Icon as={Target} boxSize="3" mr="1" />Specialized
    </Badge>
  ) : (
    <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2.5" data-testid="scope-badge-core">
      <Icon as={Globe} boxSize="3" mr="1" />Core
    </Badge>
  );

function TemplateCard({ t, actions, subtitle }) {
  return (
    <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4" data-testid={`template-card-${t.id}`}>
      <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
        <Box>
          <HStack gap="2" flexWrap="wrap">
            <Text fontWeight="600" color={colors.textPrimary}>{t.title}</Text>
            <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2.5">{t.type}</Badge>
            <StatusBadge status={t.status} />
            {t.company_id === null && <ScopeBadge scope={t.scope} />}
          </HStack>
          <Text fontSize="xs" color={colors.textSecondary} mt="1">
            {(t.schema?.sections || []).length} sections · {countFields(t.schema)} fields · v{t.version}
            {subtitle}
          </Text>
        </Box>
        <HStack gap="2" flexWrap="wrap">{actions}</HStack>
      </Flex>
    </Box>
  );
}

export default function Templates() {
  const { user, companies } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("templates");
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [masters, setMasters] = useState([]);
  const [legacy, setLegacy] = useState([]);
  const [lists, setLists] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [assignCounts, setAssignCounts] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);

  const flash = useCallback((type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4500); }, []);

  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    const [{ data, error }, { data: sl }, { data: asn }] = await safeAll([
      supabase.from("templates").select("*").order("created_at", { ascending: true }),
      supabase.from("select_lists").select("*").order("name", { ascending: true }),
      supabase.from("template_assignments").select("template_id,company_id"),
    ]);
    if (error) {
      // Offline: serve the last-known-good template catalogue if we have one.
      const snap = cacheGet(user.id, "templates_admin");
      if (snap) {
        setAllTemplates(snap.rows || []);
        setMasters((snap.rows || []).filter((t) => t.company_id === null));
        setLegacy((snap.rows || []).filter((t) => t.company_id !== null));
        setLists(snap.lists || []);
        setAssignCounts(snap.counts || {});
        if (!background) setLoading(false);
        return;
      }
      flash("error", navigator.onLine ? error.message
        : "You're offline — the template catalogue hasn't loaded on this device yet.");
    }
    const rows = data || [];
    setAllTemplates(rows);
    setMasters(rows.filter((t) => t.company_id === null));
    setLegacy(rows.filter((t) => t.company_id !== null));
    setLists(sl || []);
    const counts = {};
    (asn || []).forEach((a) => { counts[a.template_id] = (counts[a.template_id] || 0) + 1; });
    setAssignCounts(counts);
    if (!error) cachePut(user.id, "templates_admin", { rows, lists: sl || [], counts });
    if (!background) setLoading(false);
  }, [flash, user.id]);

  useEffect(() => { load(); }, [load]);

  const createMaster = async () => {
    setBusy("create");
    try {
      const { data, error } = await supabase.from("templates")
        .insert({ company_id: null, type: "Inspection", title: "Untitled master template", schema: emptySchema(), status: "draft", scope: "core" })
        .select().single();
      if (error) throw error;
      navigate(`/templates/${data.id}`);
    } catch (e) { flash("error", e.message); } finally { setBusy(null); }
  };

  const duplicate = async (t) => {
    setBusy(`dup-${t.id}`);
    try {
      const { data, error } = await supabase.from("templates")
        .insert({
          company_id: null, parent_template_id: t.id, type: t.type,
          title: `${t.title} (copy)`, schema: t.schema, status: "draft", scope: t.scope,
        })
        .select().single();
      if (error) throw error;
      flash("success", `“${t.title}” duplicated as a draft — opening the copy.`);
      navigate(`/templates/${data.id}`);
    } catch (e) { flash("error", e.message); } finally { setBusy(null); }
  };

  const toggleStatus = async (t) => {
    const next = t.status === "published" ? "draft" : "published";
    const { error } = await supabase.from("templates").update({ status: next }).eq("id", t.id);
    if (error) flash("error", error.message);
    else { flash("success", next === "published" ? `“${t.title}” published.` : `“${t.title}” moved to draft.`); load(true); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete “${t.title}”? This cannot be undone. Past submissions keep their own snapshot and are not affected.`)) return;
    const { error } = await supabase.from("templates").delete().eq("id", t.id);
    if (error) flash("error", error.message);
    else { flash("success", `“${t.title}” deleted.`); load(true); }
  };

  const convertLegacy = async (t) => {
    const companyName = companies[t.company_id]?.name || "its original company";
    if (!window.confirm(`Convert “${t.title}” into a Hakina Specialized template assigned to ${companyName}?`)) return;
    setBusy(t.id);
    try {
      const origCompany = t.company_id;
      const { error: e2 } = await supabase.from("template_assignments")
        .upsert({ template_id: t.id, company_id: origCompany, assigned_by: user.id });
      if (e2) throw e2;
      const { error } = await supabase.from("templates")
        .update({ company_id: null, scope: "specialized" }).eq("id", t.id);
      if (error) {
        await supabase.from("template_assignments").delete().eq("template_id", t.id).eq("company_id", origCompany);
        throw error;
      }
      flash("success", `“${t.title}” is now a Specialized template assigned to ${companyName}.`);
      load(true);
    } catch (e) { flash("error", e.message); } finally { setBusy(null); }
  };

  const btn = { size: "xs", variant: "outline", borderColor: colors.border };

  return (
    <Box data-testid="templates-page">
      <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>Form Templates</Heading>
      <Text color={colors.textSecondary} mb="5">
        Master templates and custom lists — managed by Hakina only. Core templates are visible to every client company; Specialized templates only to the companies you assign.
      </Text>

      <Box mb="5">
        <SegmentedTabs tabs={TABS} value={tab} onChange={setTab} />
      </Box>

      <Banner msg={msg} />

      {loading ? (
        <Flex minH="30vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>
      ) : tab === "lists" ? (
        <ListManager isSuperAdmin activeCompanyId={null} companyName={null}
          lists={lists} templates={allTemplates} reload={() => load(true)} flash={flash} />
      ) : (
        <Stack gap="8">
          {/* Master templates */}
          <Box>
            <Flex justify="space-between" align="center" mb="3" flexWrap="wrap" gap="2">
              <HStack gap="2">
                <Icon as={ShieldCheck} boxSize="5" color={colors.teal} />
                <Text fontWeight="700" color={colors.textPrimary}>Hakina Master Templates</Text>
              </HStack>
              <Button onClick={createMaster} loading={busy === "create"} data-testid="new-master-btn">
                <Plus size={16} /> New master template
              </Button>
            </Flex>
            {masters.length === 0 ? (
              <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4" data-testid="masters-empty">
                <Text color={colors.textSecondary} fontSize="sm">No master templates yet.</Text>
              </Box>
            ) : (
              <Stack gap="3">
                {masters.map((t) => (
                  <TemplateCard key={t.id} t={t}
                    subtitle={t.scope === "specialized" && (
                      <> · assigned to {assignCounts[t.id] || 0} {(assignCounts[t.id] || 0) === 1 ? "company" : "companies"}</>
                    )}
                    actions={
                      <>
                        <Button {...btn} onClick={() => setPreview(t)} data-testid={`preview-template-${t.id}`}><Eye size={14} /> Preview</Button>
                        <Button {...btn} onClick={() => navigate(`/templates/${t.id}`)} data-testid={`edit-template-${t.id}`}><Pencil size={14} /> Edit</Button>
                        <Button {...btn} onClick={() => duplicate(t)} loading={busy === `dup-${t.id}`} data-testid={`duplicate-template-${t.id}`}><Copy size={14} /> Duplicate</Button>
                        <Button {...btn} color={t.status === "published" ? colors.pendingText : colors.sageDeep} onClick={() => toggleStatus(t)} data-testid={`toggle-status-${t.id}`}>
                          {t.status === "published" ? "Unpublish" : "Publish"}
                        </Button>
                        <Button {...btn} color={colors.poppy} onClick={() => remove(t)} data-testid={`delete-template-${t.id}`}><Trash2 size={14} /></Button>
                      </>
                    } />
                ))}
              </Stack>
            )}
          </Box>

          {/* Legacy client copies — hidden from clients, kept for Super-Admin review */}
          {legacy.length > 0 && (
            <Box data-testid="legacy-copies-section">
              <HStack gap="2" mb="1">
                <Icon as={Building2} boxSize="5" color={colors.sageDeep} />
                <Text fontWeight="700" color={colors.textPrimary}>Legacy Client Copies</Text>
              </HStack>
              <Text fontSize="sm" color={colors.textSecondary} mb="3">
                Templates previously cloned by clients. They are hidden from all client users — convert one to a Specialized template to hand it back, or delete it.
              </Text>
              <Stack gap="3">
                {legacy.map((t) => (
                  <TemplateCard key={t.id} t={t}
                    subtitle={<> · owned by {companies[t.company_id]?.name || "unknown company"}</>}
                    actions={
                      <>
                        <Button {...btn} onClick={() => setPreview(t)} data-testid={`preview-template-${t.id}`}><Eye size={14} /> Preview</Button>
                        <Button {...btn} bg={colors.tealTint} borderColor={colors.teal}
                          onClick={() => convertLegacy(t)} loading={busy === t.id} data-testid={`convert-template-${t.id}`}>
                          <ArrowUpRight size={14} /> Convert to Specialized
                        </Button>
                        <Button {...btn} color={colors.poppy} onClick={() => remove(t)} data-testid={`delete-template-${t.id}`}><Trash2 size={14} /></Button>
                      </>
                    } />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      {preview && <TemplatePreview template={preview} lists={lists} onClose={() => setPreview(null)} />}
    </Box>
  );
}
